import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DocumentListView from '@components/documents/DocumentListView';
import DocumentGridView from '@components/documents/DocumentGridView';
import type { DocumentCardDocument } from '@components/documents/DocumentCard';
import DocumentFilterSidebar from '@components/documents/DocumentFilterSidebar';
import DocumentsToolbar, { DocumentViewMode } from '@components/documents/DocumentsToolbar';
import { countActiveFilters, useDocumentsQuery } from '@hooks/useDocumentsQuery';
import { useDocumentDownload } from '@hooks/useDocumentDownload';
import { useAppDispatch } from '@store/index';
import { showNotification } from '@features/ui/uiSlice';
import { resolveApiErrorMessage } from '@utils/apiErrorMessages';

const DocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<DocumentViewMode>('list');
  const [filterOpen, setFilterOpen] = useState(false);
  const documents = useDocumentsQuery();
  const dispatch = useAppDispatch();
  const download = useDocumentDownload();
  const hasDeletableDocument = documents.documents.some((document) => document.canDelete);

  // Only a fallback name: the server names the file for the version it sends.
  const handleDownloadDocument = (document: DocumentCardDocument) =>
    download(document.id, document.fileName || document.title);

  const handleDeleteDocument = async (documentId: string) => {
    try {
      await documents.removeDocument(documentId);
      dispatch(showNotification({
        type: 'success',
        messageKey: 'documents.deleteSuccess',
      }));
    } catch (error) {
      dispatch(showNotification({
        type: 'error',
        message: resolveApiErrorMessage(t, error, {
          fallbackKey: 'documents.deleteFailed',
        }),
      }));
    }
  };

  const viewProps = {
    documents: documents.documents,
    loading: documents.loading,
    onDocumentClick: (id: string) => navigate(`/documents/${id}`),
    searchQuery: documents.pendingSearch,
    filters: documents.filters,
    page: documents.page,
    pageSize: documents.pageSize,
    totalCount: documents.totalCount,
    onPageChange: documents.goToPage,
    onPageSizeChange: documents.setPageSize,
    onDelete: handleDeleteDocument,
    canDeleteDocument: (document: { canDelete?: boolean }) => Boolean(document.canDelete),
  };

  return (
    <div className="flex h-full min-w-0 gap-4">
      <DocumentFilterSidebar
        open={filterOpen}
        documents={documents.documents}
        filters={documents.filters}
        onFilterChange={documents.setFilters}
        onClose={() => setFilterOpen(false)}
      />

      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-balance sm:text-3xl">
              {t('documents.title')}
            </h1>
            {!documents.error && !documents.loading && <p className="mt-1 text-sm revy-figure text-muted-foreground">
              {t('documents.countLabel', { count: documents.totalCount })}
            </p>}
          </div>
          <Button onClick={() => navigate('/documents/upload')}>
            <Plus strokeWidth={2} />
            {t('documents.uploadDocument')}
          </Button>
        </header>

        <DocumentsToolbar
          searchQuery={documents.searchQuery}
          onSearchChange={documents.setSearchQuery}
          filterOpen={filterOpen}
          onToggleFilter={() => setFilterOpen((open) => !open)}
          activeFilterCount={countActiveFilters(documents.filters)}
          sortBy={documents.sortBy}
          sortOrder={documents.sortOrder}
          onSortChange={documents.changeSort}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />

        {!hasDeletableDocument && (
          <p className="text-xs text-muted-foreground">
            {t('documents.deletePermissionHint')}
          </p>
        )}

        {/* Both views answer the same query; sharing one object makes that
            structural rather than something a reviewer has to diff by eye. */}
        {documents.error ? (
          <div role="alert" className="rounded-lg border p-6 text-center">
            <p>{resolveApiErrorMessage(t, documents.error)}</p>
            <Button className="mt-3" variant="outline" onClick={documents.reload} disabled={documents.loading}>
              {t('common.retry')}
            </Button>
          </div>
        ) : viewMode === 'list' ? (
          <DocumentListView {...viewProps} onUpdateTags={documents.updateTags}
            sortBy={documents.sortBy} sortOrder={documents.sortOrder} onSortChange={documents.changeSort} />
        ) : (
          <DocumentGridView {...viewProps} onDownload={handleDownloadDocument} />
        )}
      </div>
    </div>
  );
};

export default DocumentsPage;
